/**
 * Clamps the collection long description and reveals its control.
 *
 * PROGRESSIVE ENHANCEMENT, AND THE DIRECTION MATTERS. The section renders the copy in full with
 * the button `hidden`; this module adds the clamp and unhides the button. Done the other way — a
 * clamp in the stylesheet that script removes — a missing or blocked module leaves a 220px stub of
 * a 12,000-character description with no way past it, and nothing on the page says so. Same
 * fail-open reasoning as assets/age-check.js.
 *
 * The expanded state sets no max-height at all. Live's equivalent expands to 2000px, which is
 * shorter than this copy on the collections that carry the most of it, so its own "Read more"
 * clips the FAQ it was revealing.
 */
class CollectionLongDescription extends HTMLElement {
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('[data-more]');
    if (!this.#button) return;

    // Nothing is clamped until we know we can unclamp it.
    this.classList.add('is-clamped');
    this.#button.hidden = false;
    this.#button.addEventListener('click', () => this.#toggle());

    // A max-height clamp hides content VISUALLY and nothing else: the copy below the fold keeps
    // its links, and those links stay in the tab order while being invisible, which is WCAG 2.4.7
    // failed by a rule that looks purely cosmetic. Expanding on focusin means focus can never come
    // to rest on something the reader cannot see. Live has this bug; this is the one place worth
    // diverging from it.
    this.addEventListener('focusin', (event) => {
      if (event.target !== this.#button && this.classList.contains('is-clamped')) this.#expand();
    });
  }

  #expand() {
    this.classList.remove('is-clamped');
    this.#button.setAttribute('aria-expanded', 'true');
    this.#button.textContent = this.#button.dataset.labelLess;
  }

  #toggle() {
    const clamped = this.classList.toggle('is-clamped');
    this.#button.setAttribute('aria-expanded', String(!clamped));
    const { labelMore, labelLess } = this.#button.dataset;
    this.#button.textContent = clamped ? labelMore : labelLess;

    // Collapsing from below the fold would otherwise leave the reader somewhere further down the
    // page than the control they just pressed, with no visible cue for what moved.
    if (clamped) {
      this.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }
}

if (!customElements.get('collection-long-description')) {
  customElements.define('collection-long-description', CollectionLongDescription);
}
